import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { SystemService } from 'app/services/system.service';
import { LocalStorageService } from '../services/local-storage.service';
import { OpenTelemetryService } from '../services/otel.service';
import { ObjectLabel } from './storage-api/object-label.service';

export interface QueryParams {
  [key: string]: string | string[] | number | boolean;
}

export interface BodyParams {
  [key: string]: any;
}

export interface Headers {
  [key: string]: string;
}

export class WrappedRequest<T> {
  private _promise: Promise<T>;
  private isLoading;

  constructor(promise?) {
    if (promise) {
      this.request = promise;
    }
  }

  set request(promise: Promise<any>) {
    this._promise = promise;
    this.isLoading = true;
    promise.finally(() => (this.isLoading = false));
  }

  get request() {
    return this._promise;
  }

  isInProgress(): boolean {
    return this.isLoading;
  }
}

@Injectable()
export abstract class APIService {
  private otelService = new OpenTelemetryService();

  private localStorageService = inject(LocalStorageService);
  get localStorage() {
    return this.localStorageService;
  }

  constructor(
    private httpClient: HttpClient,
    protected systemService: SystemService
  ) {}

  protected abstract getBaseUrl(): string;
  protected getBaseHeaders(): Headers | void {}

  public static TOKEN() {
    return localStorage.getItem('ssv-token');
  }

  public getToken(): string {
    return this.localStorage.getItem('ssv-token');
  }

  public setToken(token: string): void {
    this.localStorage.setItem('ssv-token', token, 'local', true);
  }

  private getHttpHeaders(headers?: Headers): HttpHeaders {
    let allHeaders = this.getBaseHeaders() || {};
    if (headers) {
      allHeaders = Object.assign(headers, allHeaders);
    }

    for (const header in allHeaders) {
      if (allHeaders.hasOwnProperty(header) && !allHeaders[header]) {
        delete allHeaders[header];
      }
    }

    const span = this.otelService.getActiveSpan();
    if (span) {
      allHeaders['traceparent'] = `00-${span.spanContext().traceId}-${
        span.spanContext().spanId
      }-01`;
    }

    return new HttpHeaders(allHeaders);
  }

  private getHttpUrl(endpoint: string, params?: QueryParams): string {
    let url = this.getBaseUrl() + endpoint;

    if (params) {
      const parts = [];

      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          const value = params[key];
          if (value !== null && typeof value !== 'undefined') {
            if (Array.isArray(value)) {
              value.forEach((v) => {
                parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(v));
              });
            } else {
              parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value.toString()));
            }
          }
        }
      }

      if (parts.length > 0) {
        url += '?' + parts.join('&');
      }
    }

    return url;
  }

  protected getBinary(endpoint: string, params?: QueryParams, headers?: Headers): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(this.getHttpUrl(endpoint, params), {
          headers: this.getHttpHeaders(headers),
          responseType: 'blob',
        })
        .subscribe(
          (result) => {
            resolve(result);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  protected get<T>(endpoint: string, params?: QueryParams, headers?: Headers): Promise<T> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .get<T>(this.getHttpUrl(endpoint, params), { headers: this.getHttpHeaders(headers) })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected getText(endpoint: string, params?: QueryParams, headers?: Headers): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(this.getHttpUrl(endpoint, params), {
          headers: this.getHttpHeaders(headers),
          observe: 'body',
          responseType: 'text',
        })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected post<T>(
    endpoint: string,
    body?: BodyParams,
    headers?: Headers,
    params?: QueryParams
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .post<T>(this.getHttpUrl(endpoint, params), body, { headers: this.getHttpHeaders(headers) })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected postText(
    endpoint: string,
    body?: BodyParams,
    headers?: Headers,
    params?: QueryParams
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(this.getHttpUrl(endpoint, params), body, {
          headers: this.getHttpHeaders(headers),
          observe: 'body',
          responseType: 'text',
        })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected put<T>(endpoint: string, params?: BodyParams, headers?: Headers): Promise<T> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .put<T>(this.getHttpUrl(endpoint), params, { headers: this.getHttpHeaders(headers) })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected delete<T>(
    endpoint: string,
    params?: QueryParams,
    headers?: Headers,
    bodyParams?: BodyParams | any[]
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .delete<T>(this.getHttpUrl(endpoint, params), {
          headers: this.getHttpHeaders(headers),
          body: bodyParams,
        })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected patch<T>(endpoint: string, params?: BodyParams | any[], headers?: Headers): Promise<T> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .patch<T>(this.getHttpUrl(endpoint), params, { headers: this.getHttpHeaders(headers) })
        .subscribe(
          (r) => {
            resolve(r);
          },
          (e) => {
            reject(e);
          }
        );
    });
  }

  protected static mapObjectLabels<T extends HasProperty<'objectLabels', string[] | ObjectLabel[]>>(
    item: T,
    labels?: ObjectLabel[]
  ): ObjectLabel[] {
    if (item.objectLabels && labels) {
      return item.objectLabels
        .map((al) => labels.find((l) => al === l.id))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'accent' }));
    }
    return [];
  }
}

type HasProperty<K extends PropertyKey, V> = {
  [P in K]?: V;
};
